import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import api, { formatPrice } from "../lib/api";
import { toast } from "sonner";

export default function Checkout() {
  const { items, subtotal } = useCart();
  const navigate = useNavigate();
  const [settings, setSettings] = useState({ shipping_flat: 8, free_shipping_threshold: 75 });
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "", email: "", phone: "",
    line1: "", line2: "", city: "", state: "", zip: "", country: "US",
  });

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data));
  }, []);

  useEffect(() => {
    if (items.length === 0) navigate("/shop");
  }, [items, navigate]);

  const shipping = subtotal >= (settings.free_shipping_threshold || 75) ? 0 : settings.shipping_flat || 8;
  const total = subtotal + shipping;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.line1 || !form.city) {
      toast.error("Please fill in your name, email and shipping address.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        items: items.map((i) => ({ product_id: i.product_id, variant: i.variant || null, quantity: i.quantity })),
        customer: {
          name: form.name, email: form.email, phone: form.phone,
          address: { line1: form.line1, line2: form.line2, city: form.city, state: form.state, zip: form.zip, country: form.country },
        },
        origin_url: window.location.origin,
      };
      const res = await api.post("/checkout/create-session", payload);
      window.location.href = res.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start checkout. Please try again.");
      setSubmitting(false);
    }
  };

  const inputCls = "w-full border border-[#E6DFD3] rounded-sm px-4 py-3 bg-white focus:outline-none focus:ring-2 focus:ring-[#B85C38]/40 focus:border-[#B85C38] text-[#2D372B]";

  return (
    <div className="px-6 md:px-12 lg:px-20 max-w-6xl mx-auto py-12 md:py-16">
      <h1 className="font-serif text-4xl sm:text-5xl text-[#2D372B] tracking-tight mb-10">Checkout</h1>
      <div className="grid lg:grid-cols-5 gap-10">
        <form onSubmit={submit} className="lg:col-span-3 space-y-8" data-testid="checkout-form">
          <div>
            <p className="kicker mb-4">Contact</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <input data-testid="checkout-name" className={inputCls} placeholder="Full name *" value={form.name} onChange={set("name")} />
              <input data-testid="checkout-email" type="email" className={inputCls} placeholder="Email *" value={form.email} onChange={set("email")} />
              <input data-testid="checkout-phone" className={`${inputCls} sm:col-span-2`} placeholder="Phone" value={form.phone} onChange={set("phone")} />
            </div>
          </div>
          <div>
            <p className="kicker mb-4">Shipping Address</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <input data-testid="checkout-line1" className={`${inputCls} sm:col-span-2`} placeholder="Address *" value={form.line1} onChange={set("line1")} />
              <input data-testid="checkout-line2" className={`${inputCls} sm:col-span-2`} placeholder="Apt, suite (optional)" value={form.line2} onChange={set("line2")} />
              <input data-testid="checkout-city" className={inputCls} placeholder="City *" value={form.city} onChange={set("city")} />
              <input data-testid="checkout-state" className={inputCls} placeholder="State" value={form.state} onChange={set("state")} />
              <input data-testid="checkout-zip" className={inputCls} placeholder="ZIP" value={form.zip} onChange={set("zip")} />
              <input data-testid="checkout-country" className={inputCls} placeholder="Country" value={form.country} onChange={set("country")} />
            </div>
          </div>
          <button
            type="submit"
            disabled={submitting}
            data-testid="place-order-button"
            className="w-full bg-[#B85C38] hover:bg-[#9A4A2B] disabled:opacity-60 text-white uppercase tracking-widest text-sm py-4 transition-colors rounded-sm"
          >
            {submitting ? "Redirecting to payment…" : `Pay ${formatPrice(total)}`}
          </button>
          <p className="text-xs text-[#7C897B] text-center">Secure payment powered by Stripe. Guest checkout — no account needed.</p>
        </form>

        <div className="lg:col-span-2">
          <div className="bg-white border border-[#E6DFD3] rounded-sm p-6 sticky top-28" data-testid="order-summary">
            <p className="kicker mb-4">Order Summary</p>
            <div className="space-y-4 max-h-72 overflow-y-auto">
              {items.map((i) => (
                <div key={i.key} className="flex gap-3">
                  <img src={i.image} alt={i.name} className="w-14 h-14 object-cover rounded-sm border border-[#E6DFD3]" />
                  <div className="flex-1 text-sm">
                    <p className="text-[#2D372B] font-medium leading-tight">{i.name}</p>
                    {i.variant && <p className="text-[#7C897B] text-xs">{i.variant}</p>}
                    <p className="text-[#7C897B] text-xs">Qty {i.quantity}</p>
                  </div>
                  <span className="text-sm text-[#2D372B]">{formatPrice(i.unit_price * i.quantity)}</span>
                </div>
              ))}
            </div>
            <div className="border-t border-[#E6DFD3] mt-5 pt-5 space-y-2 text-sm">
              <div className="flex justify-between text-[#4A5D4E]"><span>Subtotal</span><span>{formatPrice(subtotal)}</span></div>
              <div className="flex justify-between text-[#4A5D4E]"><span>Shipping</span><span>{shipping === 0 ? "Free" : formatPrice(shipping)}</span></div>
              <div className="flex justify-between text-[#2D372B] font-serif text-xl pt-2"><span>Total</span><span data-testid="order-total">{formatPrice(total)}</span></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
